import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas';
import {
	Coordinate,
	IChartApi,
	ISeriesApi,
	ISeriesPrimitive,
	IPrimitivePaneRenderer,
	IPrimitivePaneView,
	SeriesOptionsMap,
	PrimitivePaneViewZOrder,
	SeriesType,
	Time,
} from 'lightweight-charts';

class PaneLabelsRenderer implements IPrimitivePaneRenderer {
	_options: PaneLabelsOptions

	constructor(options: PaneLabelsOptions) {
		this._options = options;
	}
	draw(target: CanvasRenderingTarget2D) {
		target.useBitmapCoordinateSpace(scope =>
			this._drawImpl(scope)
		);
	}

	_drawImpl(scope: BitmapCoordinatesRenderingScope) {
		const { width } = scope.bitmapSize;
		const { labelData } = this._options;
		const ctx = scope.context;

		const shiftLeft = 8 * scope.horizontalPixelRatio;
		const commonY = 5 * scope.verticalPixelRatio;

		labelData.forEach((lbData: PaneLabelData) => {
			try {
				const x = (lbData.time === Number.NEGATIVE_INFINITY)
					? 0
					: (lbData.time === Number.POSITIVE_INFINITY)
						? width - (1 * scope.horizontalPixelRatio)
						: scope.horizontalPixelRatio * (this._options.timeToCoordinate(lbData.time as Time) ?? NaN);

				const dpr = window.devicePixelRatio || 1;
				const fontSize = 12 * dpr;
				ctx.font = `${fontSize}px Arial`;

				const text = lbData.text;
				const textWidth = ctx.measureText(text).width;
				const paddingX = 4 * dpr;
				const paddingY = 2 * dpr;
				const rectWidth = textWidth + 2 * paddingX;
				const rectHeight = fontSize + 2 * paddingY;
				const radius = 4 * dpr;

				ctx.save();
				ctx.translate(x, commonY);
				ctx.rotate(-Math.PI / 2);

				// Background with rounded corners
				ctx.fillStyle = lbData.backColor ?? 'white';
				drawRoundedRect(
					ctx,
					-textWidth - paddingX,
					-rectHeight / 2,
					rectWidth,
					rectHeight,
					radius
				);

				// Text
				ctx.textAlign = 'left';
				ctx.textBaseline = 'middle';
				ctx.fillStyle = lbData.textColor ?? '#fff';
				ctx.fillText(text, -textWidth, 0);

				ctx.restore();
			} catch (error) {
				console.error(error);
			}
		});
	}


	_applyTextAlignMents(ctx: CanvasRenderingContext2D, horizontalAlign: 'left' | 'right' | 'center', verticalAlign: 'top' | 'bottom' | 'center') {
		// Set text alignment based on horizontalAlign
		switch (horizontalAlign) {
			case 'left':
				ctx.textAlign = 'left';
				break;
			case 'right':
				ctx.textAlign = 'right';
				break;
			case 'center':
				ctx.textAlign = 'center';
				break;
			default:
				ctx.textAlign = 'left';
				return;
		}

		// Set text baseline based on verticalAlign
		switch (verticalAlign) {
			case 'top':
				ctx.textBaseline = 'bottom';
				break;
			case 'bottom':
				ctx.textBaseline = 'top';
				break;
			case 'center':
				ctx.textBaseline = 'middle';
				break;
			default:
				ctx.textBaseline = 'bottom';
				return;
		}
	}
}

class LabelsPaneView implements IPrimitivePaneView {
	_source: PaneLabelsPrimitive;
	_options: PaneLabelsOptions;
	_renderer: PaneLabelsRenderer;

	constructor(source: PaneLabelsPrimitive, options: PaneLabelsOptions) {
		this._source = source;
		this._options = options;
		this._renderer = new PaneLabelsRenderer(this._options);
	}

	zOrder(): PrimitivePaneViewZOrder {
		return 'top'
	}

	renderer() {
		return this._renderer
	}
}

export interface PaneLabelData {
	price: number,
	time: number,
	text: string,
	textHeight: number,
	paddingX?: number,
	paddingY?: number,
	verticalAlign: 'top' | 'bottom' | 'center',
	horizontalAlign: 'left' | 'right' | 'center',
	textColor?: string,
	backColor?: string,
}

export interface PaneLabelsOptions {
	visible?: boolean,
	labelData: PaneLabelData[],
	priceToCoordinate: (price: number) => Coordinate | null;
	timeToCoordinate: (time: Time) => Coordinate | null;
}

const defaultOptions: PaneLabelsOptions = {
	visible: true,
	labelData: [],
	priceToCoordinate: () => null,
	timeToCoordinate: () => null,
};

export class PaneLabelsPrimitive implements ISeriesPrimitive<Time> {
	_chart: IChartApi;
	_series: ISeriesApi<keyof SeriesOptionsMap>;
	_paneViews: LabelsPaneView[];
	_options: PaneLabelsOptions

	constructor(
		chart: IChartApi,
		series: ISeriesApi<SeriesType>,
		options?: Partial<PaneLabelsOptions>
	) {
		this._options = {
			...defaultOptions,
			...options,
		};
		this._chart = chart;
		this._series = series;
		this._paneViews = [new LabelsPaneView(this, this._options)];
	}

	applyOptions(options: Partial<PaneLabelsOptions>) {
		this._options = {
			...this._options,
			...options
		}
		this._paneViews = [new LabelsPaneView(this, this._options)];
	}

	updateAllViews() {

	}

	paneViews() {
		return this._paneViews;
	}
}


function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
	const r = Math.min(radius, width / 2, height / 2); // clamp radius
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.lineTo(x + width - r, y);
	ctx.quadraticCurveTo(x + width, y, x + width, y + r);
	ctx.lineTo(x + width, y + height - r);
	ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
	ctx.lineTo(x + r, y + height);
	ctx.quadraticCurveTo(x, y + height, x, y + height - r);
	ctx.lineTo(x, y + r);
	ctx.quadraticCurveTo(x, y, x + r, y);
	ctx.closePath();
	ctx.fill();
}